import React, { useState, useEffect, useRef, useContext } from 'react';
import { LanguageSelectorStyles as styles } from '../css/styles';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { FaChevronDown } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import MobileModal from './MobileModal';
import classNames from 'classnames';
import { FLAG_COMPONENTS } from './Flags';

interface LanguageSelectorProps {
  appearance?: 'icon' | 'dropdown';
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'es', label: 'Español' },
];

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ appearance = 'dropdown' }) => {

  const { currentUser } = useContext(AuthContext);
  const { i18n } = useTranslation();
  const { language, changeLanguage, changeUserLanguage } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChangeLanguage = (newLanguage: string) => {
    if (currentUser && currentUser.userId && appearance === 'dropdown') {
      changeUserLanguage(newLanguage, currentUser.userId);
    } else {
      changeLanguage(newLanguage);
    }

    i18n.changeLanguage(newLanguage);
    setShowDropdown(false);
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowDropdown(false);
    }
  }

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick)
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick)
    };
  }, [showDropdown])


  const CurrentFlag = FLAG_COMPONENTS[language] ?? FLAG_COMPONENTS.en;

  if (isMobile) {
    return (
      <>
        <button className={styles.languageButton} onClick={() => setShowDropdown(true)}>
          <CurrentFlag className={styles.flag} />
        </button>

        <MobileModal isOpen={showDropdown} onClose={() => setShowDropdown(false)} header={"Select Language"}>
          <div className="language-selector-mobile-buttons">
            {LANGUAGES.map(({ code, label }) => {
              const Flag = FLAG_COMPONENTS[code];
              return (
                <button
                  key={code}
                  className={classNames("global-button", styles.mobileLanguageOption, { [styles.mobileLanguageOptionActive]: language === code })}
                  onClick={() => handleChangeLanguage(code)}
                >
                  <Flag className={styles.flag} />
                  {label}
                </button>
              );
            })}
          </div>
        </MobileModal>
      </>
    );
  }

  if (appearance === 'icon') {
    return (
      <div className={styles.iconWrapper} ref={dropdownRef}>
        <button
          type="button"
          className={classNames(styles.trigger, { [styles.triggerOpen]: showDropdown })}
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <CurrentFlag className={styles.flag} />
          <FaChevronDown className={styles.chevron} />
        </button>
        <div className={classNames(styles.dropdownMenu, { [styles.dropdownMenuShow]: showDropdown })}>
          {LANGUAGES.map(({ code, label }) => {
            const Flag = FLAG_COMPONENTS[code];
            return (
              <button
                key={code}
                className={classNames(styles.dropdownOption, { [styles.dropdownOptionActive]: language === code })}
                onClick={() => handleChangeLanguage(code)}
              >
                <Flag className={styles.flag} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <select className={styles.selectLanguage} value={language} onChange={(e) => handleChangeLanguage(e.target.value)} name="studentLanguage">
        <option value="en">English</option>
        <option value="ru">Русский</option>
        <option value="es">Español</option>
      </select>
    </div>
  )
}
export default LanguageSelector